import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtNZD, fmtDate, todayDMY, toISODate, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StageSelect from "@/components/StageSelect";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";

// --- QuoteAttachments: list / upload / open / remove files for a quote ---
function QuoteAttachments({ projectId, quoteId, legacyUrl, allowLegacyToggle }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  async function load() {
    try { const { data } = await api.get(`/projects/${projectId}/subcontractor-quotes/${quoteId}/attachments`); setFiles(data || []); } catch { setFiles([]); }
  }
  useEffect(() => { if (quoteId) load(); /* eslint-disable-next-line */ }, [quoteId]);
  async function onPick(e) {
    const fs = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fs.length) return;
    setBusy(true);
    for (const f of fs) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`/projects/${projectId}/subcontractor-quotes/${quoteId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (err) { alert(err?.response?.data?.detail || "Upload failed"); }
    }
    setBusy(false); load();
  }
  async function remove(fid) {
    if (!window.confirm("Remove this file?")) return;
    await api.delete(`/projects/${projectId}/subcontractor-quotes/${quoteId}/attachments/${fid}`); load();
  }
  function openFile(f) {
    const url = `${BACKEND_URL}/api/projects/${projectId}/subcontractor-quotes/${quoteId}/attachments/${f.id}`;
    window.open(url, "_blank", "noopener");
  }
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`q-attach-${quoteId}`}>
      {files.map(f => (
        <span key={f.id} className="inline-flex items-center gap-1 border border-neutral-700 rounded px-1.5 py-0.5 bg-neutral-900 text-[11px]" data-testid={`q-file-${f.id}`}>
          <button className="hub-link truncate max-w-[180px]" title={f.filename} onClick={() => openFile(f)}>{f.filename}</button>
          <button className="text-red-400 opacity-70 hover:opacity-100" title="Remove" onClick={() => remove(f.id)} data-testid={`q-file-del-${f.id}`}>×</button>
        </span>
      ))}
      <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt" className="hidden" onChange={onPick} data-testid={`q-attach-file-${quoteId}`}/>
      <button className="text-[11px] btn-ghost px-2 py-0.5" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`q-attach-btn-${quoteId}`}>{busy ? "Uploading…" : "+ Attach file"}</button>
      {legacyUrl && allowLegacyToggle && (
        <a className="hub-link text-[11px]" href={legacyUrl} target="_blank" rel="noreferrer" title="Legacy pasted link">link</a>
      )}
    </div>
  );
}

/**
 * Project > Subcontractors tab (Admin/Manager).
 *
 * Columns:
 *   left — trades list (seeded 30 trades), status chips, suggested-trade reasons
 *   centre — selected trade's quotes (shortlisted companies + inline quote entry)
 *   right — compare side-by-side & push selection into Estimate.
 */
export default function SubcontractorsTab() {
  const { project } = useOutletContext();
  const { user } = useAuth();
  const isMgmt = user?.role === "Admin" || user?.role === "Manager";
  const [trades, setTrades] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [stages, setStages] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [msg, setMsg] = useState("");
  const [adding, setAdding] = useState(false);
  const [showAddTrade, setShowAddTrade] = useState(false);
  const [newTradeName, setNewTradeName] = useState("");
  const [editing, setEditing] = useState(null);
  const [undoVersion, setUndoVersion] = useState(null);
  // Pending files chosen BEFORE the quote is saved (uploaded after POST)
  const [pendingFiles, setPendingFiles] = useState([]);
  const pendingInputRef = useRef(null);

  async function load() {
    const [t, q, s, sug, st] = await Promise.all([
      api.get("/trades"),
      api.get(`/projects/${project.id}/subcontractor-quotes`),
      api.get("/suppliers"),
      api.get(`/projects/${project.id}/suggested-trades`),
      api.get(`/projects/${project.id}/stages`).catch(() => ({ data: [] })),
    ]);
    setTrades(t.data || []); setQuotes(q.data || []); setSuppliers(s.data || []);
    setSuggested(sug.data || []); setStages(st.data || []);
    if (!selectedTrade && (t.data || []).length) setSelectedTrade(t.data[0].name);
  }
  useEffect(() => { if (project?.id) load(); /* eslint-disable-next-line */ }, [project?.id]);

  function flash(m) { setMsg(m); setTimeout(() => setMsg(""), 3000); }

  const byTrade = useMemo(() => {
    const m = {};
    for (const q of quotes) {
      (m[q.trade] = m[q.trade] || []).push(q);
    }
    return m;
  }, [quotes]);

  function statusOf(tradeName) {
    const qs = byTrade[tradeName] || [];
    if (qs.some(q => q.selected)) return "Selected";
    if (qs.length) return "Quotes received";
    return "No quotes";
  }

  function chipClass(s) {
    return s === "Selected" ? "bg-silver text-black"
      : s === "Quotes received" ? "border border-silver text-silver"
      : "border border-neutral-700 text-neutral-400";
  }

  const tradeQuotes = (byTrade[selectedTrade] || []).slice()
    .sort((a, b) => (a.amount_ex || 0) - (b.amount_ex || 0));

  // Suppliers that match the selected trade — Luke never invents companies.
  const matchingSuppliers = useMemo(() => {
    if (!selectedTrade) return [];
    const q = selectedTrade.toLowerCase();
    return suppliers.filter(s => (s.trade || "").toLowerCase().includes(q)
                              || q.split(/\s|&/).some(w => w && (s.trade || "").toLowerCase().includes(w)));
  }, [suppliers, selectedTrade]);

  const [form, setForm] = useState({
    company: "", amount_ex: "", inclusions: "", exclusions: "",
    quoted_at: todayDMY(), valid_until: "", attachment_url: "", stage_id: "",
  });
  function resetForm() { setForm({ company: "", amount_ex: "", inclusions: "", exclusions: "", quoted_at: todayDMY(), valid_until: "", attachment_url: "", stage_id: "" }); }

  async function addQuote() {
    if (!form.company || !form.amount_ex) { flash("Pick a company and enter the amount"); return; }
    setAdding(true);
    try {
      const { data: q } = await api.post(`/projects/${project.id}/subcontractor-quotes`, {
        trade: selectedTrade, company: form.company,
        amount_ex: Math.round(parseFloat(form.amount_ex || 0) * 100) / 100,
        inclusions: form.inclusions, exclusions: form.exclusions,
        quoted_at: toISODate(form.quoted_at) || null,
        valid_until: form.valid_until ? toISODate(form.valid_until) : null,
        attachment_url: form.attachment_url || null,
        stage_id: form.stage_id || null,
      });
      // Upload any pending files chosen before Save
      for (const f of pendingFiles) {
        const fd = new FormData(); fd.append("file", f);
        try {
          await api.post(`/projects/${project.id}/subcontractor-quotes/${q.id}/attachments`, fd,
                         { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) { flash(`Upload failed for ${f.name}: ${e?.response?.data?.detail || e.message}`); }
      }
      setPendingFiles([]);
      resetForm(); await load(); flash("Quote added ✓");
    } catch (e) {
      flash(e?.response?.data?.detail || "Add failed");
    } finally { setAdding(false); }
  }

  async function saveEdit() {
    try {
      await api.patch(`/projects/${project.id}/subcontractor-quotes/${editing.id}`, {
        company: editing.company, amount_ex: parseFloat(editing.amount_ex || 0),
        inclusions: editing.inclusions, exclusions: editing.exclusions,
        quoted_at: toISODate(editing.quoted_at) || null,
        valid_until: editing.valid_until ? toISODate(editing.valid_until) : null,
        stage_id: editing.stage_id || null,
      });
      setEditing(null); await load(); flash("Saved ✓");
    } catch (e) { flash(e?.response?.data?.detail || "Save failed"); }
  }

  async function del(q) {
    if (!window.confirm(`Delete quote from "${q.company}"? Are you sure?`)) return;
    await api.delete(`/projects/${project.id}/subcontractor-quotes/${q.id}`);
    await load(); flash("Deleted ✓");
  }

  async function selectQuote(q) {
    try {
      const { data } = await api.post(`/projects/${project.id}/subcontractor-quotes/${q.id}/select`);
      if (data?.undo_version_id) setUndoVersion(data.undo_version_id);
      await load();
      flash(`"${q.company}" selected and pushed into the Estimate.`);
    } catch (e) { flash(e?.response?.data?.detail || "Select failed"); }
  }

  async function undoLastSelect() {
    if (!undoVersion) return;
    try {
      await api.post(`/projects/${project.id}/estimate/import-undo`, { version_id: undoVersion });
      setUndoVersion(null);
      flash("Estimate rolled back to the state before this selection.");
      await load();
    } catch (e) { flash(e?.response?.data?.detail || "Undo failed"); }
  }

  async function addTrade() {
    const n = newTradeName.trim();
    if (!n) { flash("Trade name is required"); return; }
    try {
      await api.post("/trades", { name: n });
      setNewTradeName(""); setShowAddTrade(false); await load();
      flash(`Trade "${n}" added ✓`);
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not add trade");
    }
  }

  async function removeTrade(name) {
    if ((byTrade[name] || []).length) { flash("Remove its quotes first before removing the trade."); return; }
    if (!window.confirm(`Remove trade "${name}" from the seeded list?`)) return;
    const t = trades.find(x => x.name === name);
    if (t) await api.delete(`/trades/${t.id}`);
    await load();
  }

  const selectedQuote = tradeQuotes.find(q => q.selected);
  // Round every money sum to cents (integer-cent accumulation) to kill float
  // drift. fmtNZD will add exactly two decimals.
  const toCents = (v) => Math.round(parseFloat(v || 0) * 100);
  const perStageSelectedTotals = useMemo(() => {
    const m = {}; let gCents = 0;
    for (const q of quotes) {
      if (!q.selected) continue;
      const key = (stages.find(s => s.id === q.stage_id)?.name) || "Unassigned";
      const c = toCents(q.amount_ex);
      m[key] = (m[key] || 0) + c;
      gCents += c;
    }
    // Convert accumulated cents -> dollars for display
    const per_stage = Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v / 100]));
    return { per_stage, grand: gCents / 100 };
  }, [quotes, stages]);

  // Header summary: entered total, selected total, count of trades.
  const headerSummary = useMemo(() => {
    const tradesWithQuotes = Object.keys(byTrade);
    const selectedCents = quotes.filter(q => q.selected).reduce((s, q) => s + toCents(q.amount_ex), 0);
    const enteredCents = quotes.reduce((s, q) => s + toCents(q.amount_ex), 0);
    const selectedTrades = tradesWithQuotes.filter(t => (byTrade[t] || []).some(q => q.selected));
    const notYetSelected = tradesWithQuotes.filter(t => !(byTrade[t] || []).some(q => q.selected));
    // Duplicate company+amount across different trades on this project
    const seen = {};
    for (const q of quotes) {
      const key = `${(q.company || "").trim().toLowerCase()}|${toCents(q.amount_ex)}`;
      if (!key.startsWith("|")) (seen[key] = seen[key] || []).push(q);
    }
    const dupKeys = Object.entries(seen).filter(([, arr]) => new Set(arr.map(q => q.trade)).size > 1);
    const dupQuoteIds = new Set();
    for (const [, arr] of dupKeys) arr.forEach(q => dupQuoteIds.add(q.id));
    return {
      selected: selectedCents / 100,
      entered: enteredCents / 100,
      trades_total: tradesWithQuotes.length,
      trades_selected: selectedTrades.length,
      trades_not_selected: notYetSelected.length,
      not_selected_names: notYetSelected,
      duplicate_pairs: dupKeys.length,
      duplicate_quote_ids: dupQuoteIds,
    };
  }, [quotes, byTrade]);

  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  async function openBulkPreview() {
    try {
      const { data } = await api.post(`/projects/${project.id}/subcontractor-quotes/select-single`, { preview: true });
      setBulkPreview(data);
    } catch (e) { flash(e?.response?.data?.detail || "Preview failed"); }
  }
  async function confirmBulkSelect() {
    try {
      const { data } = await api.post(`/projects/${project.id}/subcontractor-quotes/select-single`, { commit: true });
      setBulkAction(data); setBulkPreview(null);
      flash(`${data.count} trades selected · ${fmtNZD(data.total)}`);
      await load();
    } catch (e) { flash(e?.response?.data?.detail || "Select failed"); }
  }
  async function undoBulkSelect() {
    if (!bulkAction) return;
    try {
      await api.post(`/projects/${project.id}/subcontractor-quotes/select-single/${bulkAction.action_id}/undo`);
      setBulkAction(null); flash("Bulk selection undone");
      await load();
    } catch (e) { flash(e?.response?.data?.detail || "Undo failed"); }
  }

  if (!isMgmt) {
    return <div className="card opacity-70" data-testid="subs-tab-forbidden">Subcontractors is Admin/Manager only.</div>;
  }

  return (
    <div className="space-y-3" data-testid="subs-tab">
      {msg && <div className="text-green-400 text-sm" data-testid="subs-msg">{msg}</div>}

      {/* Header summary */}
      <div className="card" data-testid="subs-header">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              Selected: <b className="text-silver" data-testid="subs-h-selected">{fmtNZD(headerSummary.selected)}</b>
              <span className="opacity-60 ml-1" data-testid="subs-h-sel-trades">({headerSummary.trades_selected} of {headerSummary.trades_total} trades)</span>
            </div>
            <div>
              Entered: <b className="text-silver" data-testid="subs-h-entered">{fmtNZD(headerSummary.entered)}</b>
              <span className="opacity-60 ml-1">(all quotes)</span>
            </div>
            <div>
              Not yet selected: <b className="text-silver" data-testid="subs-h-not-sel">{headerSummary.trades_not_selected} trades</b>
            </div>
            {headerSummary.duplicate_pairs > 0 && (
              <div className="text-amber-300" data-testid="subs-h-dup">
                ⚠ {headerSummary.duplicate_pairs} duplicate company+amount pair{headerSummary.duplicate_pairs > 1 ? "s" : ""} across trades
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <button className="btn-silver text-xs" onClick={openBulkPreview} data-testid="btn-subs-select-single"
                    disabled={headerSummary.trades_not_selected === 0}>
              Select where only one quote
            </button>
            {bulkAction && (
              <button className="btn-ghost text-xs" onClick={undoBulkSelect} data-testid="btn-subs-bulk-undo">
                Undo last bulk select
              </button>
            )}
          </div>
        </div>
        {headerSummary.trades_not_selected > 0 && (
          <div className="text-xs opacity-70 mt-2" data-testid="subs-h-not-sel-list">
            Still to pick: {headerSummary.not_selected_names.join(", ")}
          </div>
        )}
        {/* Per-stage sub-totals of SELECTED quotes (merged in from the old separate strip) */}
        <div className="mt-2 pt-2 border-t border-neutral-900 flex items-center justify-between flex-wrap gap-2" data-testid="subs-stage-chips">
          <div className="text-xs opacity-70">By stage (selected):</div>
          <div className="text-xs flex flex-wrap gap-2">
            {Object.entries(perStageSelectedTotals.per_stage).map(([n, v]) => (
              <span key={n} className="px-2 py-0.5 border border-neutral-800 rounded">
                {n}: <span className="text-silver">{fmtNZD(v)}</span>
              </span>
            ))}
            {Object.keys(perStageSelectedTotals.per_stage).length === 0 && <span className="opacity-50">No quotes selected yet.</span>}
          </div>
          {undoVersion && (
            <button className="btn-ghost text-xs" onClick={undoLastSelect} data-testid="btn-subs-undo-select">
              Undo last selection
            </button>
          )}
        </div>
      </div>

      {/* Bulk-select preview modal */}
      {bulkPreview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="subs-bulk-modal">
          <div className="bg-neutral-900 border border-neutral-700 rounded max-w-xl w-full p-4 space-y-2">
            <h3 className="text-silver">Select {bulkPreview.count} single-quote trade{bulkPreview.count === 1 ? "" : "s"}</h3>
            <div className="text-sm">This will mark the only quote on each listed trade as Selected and push it to the Estimate. Dollar total: <b className="text-silver">{fmtNZD(bulkPreview.total)}</b></div>
            <ul className="max-h-64 overflow-y-auto text-xs divide-y divide-neutral-800" data-testid="subs-bulk-list">
              {bulkPreview.singles.map(s => (
                <li key={s.quote_id} className="py-1 flex justify-between"><span>{s.trade} — {s.company}</span><span className="text-silver">{fmtNZD(s.amount_ex)}</span></li>
              ))}
            </ul>
            {bulkPreview.trades_left_for_you.length > 0 && (
              <div className="text-xs opacity-70" data-testid="subs-bulk-left">
                Trades with several quotes (left for you): {bulkPreview.trades_left_for_you.join(", ")}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button className="btn-ghost text-sm" onClick={() => setBulkPreview(null)} data-testid="btn-subs-bulk-cancel">Cancel</button>
              <button className="btn-silver text-sm" onClick={confirmBulkSelect} disabled={bulkPreview.count === 0} data-testid="btn-subs-bulk-confirm">
                Yes, select {bulkPreview.count}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* hidden testid kept so other code that reads it doesn't blow up */}
      <span className="hidden" data-testid="subs-grand">{fmtNZD(perStageSelectedTotals.grand)}</span>

      {/* Suggested trades — two balanced columns on wide screens */}
      <div className="card" data-testid="subs-suggested">
        <div className="text-silver text-sm mb-2">Suggested trades <span className="opacity-60 text-xs">(from your stages and analysed plans)</span></div>
        {suggested.length === 0 && <div className="text-xs opacity-60">No suggestions yet — apply a build template on the Tasks tab, or analyse a plan.</div>}
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6" data-testid="subs-suggested-grid">
          {suggested.map(s => {
            const present = trades.some(t => t.name.toLowerCase() === s.trade.toLowerCase());
            const hasQuotes = (byTrade[s.trade] || []).length > 0;
            const toggle = async () => {
              if (!present) {
                try {
                  await api.post("/trades", { name: s.trade });
                  await load();
                  setSelectedTrade(s.trade);
                } catch (ex) { flash(ex?.response?.data?.detail || "Could not add trade"); }
                return;
              }
              if (hasQuotes) { flash(`"${s.trade}" has quotes — delete them first before removing the trade.`); return; }
              const t = trades.find(x => x.name.toLowerCase() === s.trade.toLowerCase());
              if (t) { await api.delete(`/trades/${t.id}`); await load(); }
            };
            return (
              <li key={s.trade} className="flex items-center gap-3 py-1.5 border-b border-neutral-900 last:border-b-0" data-testid={`subs-suggest-row-${s.trade}`}>
                <input type="checkbox" checked={present} onChange={toggle}
                       data-testid={`subs-suggest-chk-${s.trade}`}/>
                <button type="button" className="text-silver text-sm hover:underline"
                        onClick={() => setSelectedTrade(s.trade)}
                        data-testid={`subs-suggest-${s.trade}`}>{s.trade}</button>
                <span className="text-xs opacity-70">— {s.reasons.join(" / ")}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Trades list */}
        <div className="card lg:col-span-1" data-testid="subs-trades-col">
          <div className="flex items-center justify-between mb-2">
            <div className="text-silver text-sm">Trades ({trades.length})</div>
            <button className="btn-ghost text-xs" onClick={() => setShowAddTrade(v => !v)} data-testid="btn-subs-add-trade">
              {showAddTrade ? "Cancel" : "+ Trade"}
            </button>
          </div>
          {showAddTrade && (
            <div className="flex gap-2 mb-2">
              <input className="input flex-1" placeholder="New trade name" value={newTradeName}
                     onChange={e => setNewTradeName(e.target.value)} data-testid="subs-new-trade"/>
              <button className="btn-silver text-xs" onClick={addTrade} data-testid="btn-subs-new-trade-save">Add</button>
            </div>
          )}
          <div className="divide-y divide-neutral-900 max-h-[480px] overflow-y-auto">
            {trades.map(t => {
              const st = statusOf(t.name);
              const sel = selectedTrade === t.name;
              return (
                <div key={t.id} data-testid={`subs-trade-${t.name}`}
                  className={`flex items-center justify-between py-2 px-2 cursor-pointer hover:bg-neutral-900 ${sel ? "bg-neutral-900" : ""}`}
                  onClick={() => setSelectedTrade(t.name)}>
                  <span className={sel ? "text-silver" : "text-white"}>{t.name}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${chipClass(st)}`} data-testid={`subs-trade-chip-${t.name}`}>{st}</span>
                    <button className="text-xs text-neutral-500 hover:text-red-400"
                            title="Remove this trade" onClick={(e) => { e.stopPropagation(); removeTrade(t.name); }}
                            data-testid={`btn-subs-remove-${t.name}`}>×</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quotes column */}
        <div className="card lg:col-span-2" data-testid="subs-quotes-col">
          <div className="flex items-center justify-between mb-2">
            <div className="text-silver text-sm">{selectedTrade || "— pick a trade —"}</div>
            {selectedQuote && <div className="text-xs text-silver">Selected: <b>{selectedQuote.company}</b></div>}
          </div>

          {selectedTrade && (
            <>
              {/* Add quote form */}
              <div className="border border-neutral-800 rounded p-2 mb-3" data-testid="subs-add-quote-form">
                <div className="text-xs text-silver mb-1">Add a quote for this trade</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <select className="input" value={form.company} onChange={e=>setForm({...form, company:e.target.value})}
                          data-testid="subs-f-company">
                    <option value="">— Shortlist from Suppliers/Subs —</option>
                    {matchingSuppliers.length === 0
                      ? suppliers.map(s => <option key={s.id} value={s.name}>{s.name} {s.trade ? `· ${s.trade}` : ""}</option>)
                      : matchingSuppliers.map(s => <option key={s.id} value={s.name}>{s.name} · {s.trade}</option>)}
                  </select>
                  <NumberInput className="input" placeholder="Amount ex GST" money
                         value={form.amount_ex} onChange={e=>setForm({...form, amount_ex:e.target.value})}
                         data-testid="subs-f-amount"/>
                  <StageSelect projectId={project.id} value={form.stage_id} stages={stages}
                    onStagesChanged={load}
                    onChange={(sid)=>setForm({...form, stage_id: sid || ""})}
                    placeholder="— Push into which stage? —"
                    data-testid="subs-f-stage"/>
                  <DateInput className="input" placeholder="Date quoted (DD/MM/YYYY)" format="dmy" value={form.quoted_at}
                         onChange={e=>setForm({...form, quoted_at:e.target.value})} data-testid="subs-f-date"/>
                  <DateInput className="input" placeholder="Valid until (DD/MM/YYYY)" format="dmy" value={form.valid_until}
                         onChange={e=>setForm({...form, valid_until:e.target.value})} data-testid="subs-f-valid"/>
                  <input className="input" placeholder="PDF link (optional, legacy)" value={form.attachment_url}
                         onChange={e=>setForm({...form, attachment_url:e.target.value})} data-testid="subs-f-pdf"
                         title="For back-compat only. New quotes should use 'Attach file' below."/>
                </div>
                <div className="mt-2 border border-dashed border-neutral-700 rounded p-2" data-testid="subs-f-attach-row">
                  <div className="text-xs text-silver mb-1">Attach file(s) <span className="opacity-60">— PDF / JPG / PNG / DOCX / XLSX, up to 15 MB each, uploads when you press "+ Add quote"</span></div>
                  <div className="flex flex-wrap items-center gap-1">
                    {pendingFiles.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 border border-neutral-700 rounded px-1.5 py-0.5 bg-neutral-900 text-[11px]" data-testid={`subs-f-pending-${i}`}>
                        <span className="truncate max-w-[180px]" title={f.name}>{f.name}</span>
                        <button type="button" className="text-red-400 opacity-70 hover:opacity-100" title="Remove" onClick={()=>setPendingFiles(fs => fs.filter((_, j) => j !== i))}>×</button>
                      </span>
                    ))}
                    <input ref={pendingInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt"
                           className="hidden" onChange={e => { setPendingFiles(fs => [...fs, ...Array.from(e.target.files || [])]); e.target.value = ""; }}
                           data-testid="subs-f-attach-file"/>
                    <button type="button" className="text-[11px] btn-ghost px-2 py-0.5" onClick={()=>pendingInputRef.current?.click()} data-testid="subs-f-attach-btn">+ Attach file</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  <textarea className="input" rows={2} placeholder="Inclusions"
                            value={form.inclusions} onChange={e=>setForm({...form, inclusions:e.target.value})}
                            data-testid="subs-f-inc"/>
                  <textarea className="input" rows={2} placeholder="Exclusions"
                            value={form.exclusions} onChange={e=>setForm({...form, exclusions:e.target.value})}
                            data-testid="subs-f-exc"/>
                </div>
                <div className="mt-2 text-xs opacity-60">
                  Companies come from Configuration → Suppliers & Subcontractors. Nothing invented.
                </div>
                <button className="btn-silver text-sm mt-2" onClick={addQuote} disabled={adding} data-testid="btn-subs-add-quote">
                  {adding ? "Adding…" : "+ Add quote"}
                </button>
              </div>

              {/* Side-by-side quotes */}
              {tradeQuotes.length === 0 ? (
                <div className="opacity-60 text-sm" data-testid="subs-empty-quotes">No quotes yet — add one above.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table text-sm" data-testid="subs-quotes-table">
                    <thead><tr>
                      <th>Company</th><th className="text-right">Amount ex GST</th>
                      <th>Stage</th><th>Quoted</th><th>Valid until</th>
                      <th>Inclusions / Exclusions</th><th></th>
                    </tr></thead>
                    <tbody>
                      {tradeQuotes.map(q => (
                        editing?.id === q.id ? (
                          <tr key={q.id} data-testid={`subs-q-edit-${q.id}`}>
                            <td><input className="input" value={editing.company} onChange={e=>setEditing({...editing,company:e.target.value})}/></td>
                            <td><NumberInput className="input text-right" money value={editing.amount_ex||0}
                                       onChange={e=>setEditing({...editing,amount_ex:e.target.value})}/></td>
                            <td>
                              <select className="input" value={editing.stage_id||""} onChange={e=>setEditing({...editing,stage_id:e.target.value})}>
                                <option value="">— Unassigned —</option>
                                {stages.map(s=> <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            </td>
                            <td><DateInput className="input" format="dmy" value={editing.quoted_at||""} onChange={e=>setEditing({...editing,quoted_at:e.target.value})}/></td>
                            <td><DateInput className="input" format="dmy" value={editing.valid_until||""} onChange={e=>setEditing({...editing,valid_until:e.target.value})}/></td>
                            <td>
                              <textarea className="input" rows={2} placeholder="Inclusions" value={editing.inclusions||""} onChange={e=>setEditing({...editing,inclusions:e.target.value})}/>
                              <textarea className="input mt-1" rows={2} placeholder="Exclusions" value={editing.exclusions||""} onChange={e=>setEditing({...editing,exclusions:e.target.value})}/>
                            </td>
                            <td className="flex flex-col gap-1">
                              <button className="hub-link text-xs" onClick={saveEdit} data-testid={`btn-subs-save-${q.id}`}>Save</button>
                              <button className="opacity-70 text-xs" onClick={()=>setEditing(null)}>Cancel</button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={q.id} className={q.selected ? "bg-silver/5" : ""} data-testid={`subs-q-${q.id}`}>
                            <td className={q.selected ? "text-silver" : ""}>{q.company}
                              {q.selected && <span className="ml-2 text-[10px] bg-silver text-black px-1 rounded">SELECTED</span>}
                              {headerSummary.duplicate_quote_ids.has(q.id) && (
                                <div className="text-amber-300 text-[11px] mt-0.5" data-testid={`subs-q-dup-${q.id}`} title="Same company and amount on another trade — check">
                                  ⚠ Same company and amount on another trade: check
                                </div>
                              )}
                            </td>
                            <td className="text-right">{fmtNZD(q.amount_ex)}</td>
                            <td className="opacity-80">{stages.find(s=>s.id===q.stage_id)?.name || "—"}</td>
                            <td>{fmtDate(q.quoted_at)}</td>
                            <td>{fmtDate(q.valid_until) || "—"}</td>
                            <td className="text-xs opacity-80 max-w-[320px]">
                              {q.inclusions && <div><b>Inc:</b> {q.inclusions}</div>}
                              {q.exclusions && <div><b>Exc:</b> {q.exclusions}</div>}
                              <QuoteAttachments projectId={project.id} quoteId={q.id} legacyUrl={q.attachment_url} allowLegacyToggle={!!q.attachment_url}/>
                            </td>
                            <td className="flex gap-2 text-xs">
                              <button className="hub-link" onClick={()=>setEditing({...q, quoted_at: fmtDate(q.quoted_at), valid_until: fmtDate(q.valid_until)})}
                                      data-testid={`btn-subs-edit-${q.id}`}>Edit</button>
                              <button className="text-silver" onClick={()=>selectQuote(q)} data-testid={`btn-subs-select-${q.id}`}>
                                {q.selected ? "Re-select" : "Select"}
                              </button>
                              <button className="text-red-400" onClick={()=>del(q)} data-testid={`btn-subs-del-${q.id}`}>Delete</button>
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
